import { LIMITS } from '../data/limits';
import { BUZZWORDS } from '../data/vocabulary';
import {
  charCount,
  countEmoji,
  findPhrases,
  gradient,
  isShouting,
  leadingWindow,
  normalise,
} from '../text';
import type { Rule, RuleContext } from '../types';
import { defineRule, findingsFor, flag, pass, skip } from './helpers';

/**
 * Headline rules.
 *
 * The headline is the highest-leverage field on a LinkedIn profile: it follows
 * the user's name everywhere — search results, comments, invitations, "people
 * also viewed" — usually as the *only* text a reader sees before deciding
 * whether to click. That is why it carries the most weight for most audiences.
 *
 * This file is the reference implementation for the rule pattern. See
 * `docs/ADDING_A_RULE.md`.
 */

const f = findingsFor('headline');

/** The headline as the user typed it, whitespace normalised. */
const find = (ctx: RuleContext) => normalise(ctx.profile.headline);

export const headlineRules: readonly Rule[] = [
  defineRule({
    id: 'headline.missing',
    section: 'headline',
    weight: 3,
    evaluate(ctx) {
      if (find(ctx)) return pass();
      return flag(
        0,
        f(
          'missing',
          'critical',
          'Your headline is empty',
          'LinkedIn falls back to your most recent job title, which says nothing about what you actually do. The headline is the only text that follows your name into search results, comments, and invitations.',
          'Write one line covering what you do, who for, and the value you create. A simple pattern that works: "Role | Specialisation | Outcome you deliver".'
        )
      );
    },
  }),

  defineRule({
    id: 'headline.length',
    section: 'headline',
    weight: 2,
    evaluate(ctx) {
      const headline = find(ctx);
      if (!headline) return skip(); // `headline.missing` already covers this.

      const length = charCount(headline);

      if (length > LIMITS.headline.hard) {
        return flag(
          0.2,
          f(
            'too-long',
            'critical',
            `Your headline is ${length} characters — over LinkedIn's ${LIMITS.headline.hard} limit`,
            `LinkedIn will not save anything past ${LIMITS.headline.hard} characters, so the end of your headline is being silently cut off.`,
            `Cut it to under ${LIMITS.headline.hard} characters. Lead with the part you most need people to read.`,
            [headline]
          )
        );
      }

      if (length < LIMITS.headline.minRecommended) {
        return flag(
          gradient(length, 0, LIMITS.headline.minRecommended),
          f(
            'too-short',
            'important',
            `Your headline is only ${length} characters`,
            `You have ${LIMITS.headline.hard} characters available and are using ${length}. Every unused character is a keyword a recruiter could have searched for.`,
            'Add your specialisation and the outcome you deliver. "Data Analyst" becomes "Data Analyst | SQL & Power BI | Turning messy data into decisions leaders trust".',
            [headline]
          )
        );
      }

      return pass();
    },
  }),

  defineRule({
    id: 'headline.front-loaded',
    section: 'headline',
    weight: 2,
    evaluate(ctx) {
      const headline = find(ctx);
      if (!headline) return skip();
      if (charCount(headline) <= LIMITS.headline.previewTarget) return pass();

      const preview = leadingWindow(headline, LIMITS.headline.previewTarget);
      // A separator in the visible window means at least one complete idea
      // survives truncation. Without one, the reader may see half a sentence.
      const hasCompleteIdea = /[|•·—–\-:]/.test(preview) || preview.split(' ').length >= 6;

      if (hasCompleteIdea) return pass();

      return flag(
        0.5,
        f(
          'not-front-loaded',
          'important',
          'Your headline gets cut off before it says anything',
          `Search results and mobile show roughly the first ${LIMITS.headline.previewTarget} characters. Yours currently reads as "${preview}…", which does not stand on its own.`,
          `Put your role and specialisation in the first ${LIMITS.headline.previewTarget} characters and save the supporting detail for the end.`,
          [preview]
        )
      );
    },
  }),

  defineRule({
    id: 'headline.buzzwords',
    section: 'headline',
    weight: 2,
    evaluate(ctx) {
      const headline = find(ctx);
      if (!headline) return skip();

      const matched = findPhrases(headline, BUZZWORDS);
      if (matched.length === 0) return pass();

      return flag(
        gradient(matched.length, 3, 0),
        f(
          'buzzwords',
          'important',
          `Your headline uses ${matched.length === 1 ? 'a buzzword' : 'buzzwords'} instead of evidence`,
          'These words are so common that recruiters read straight past them. They take up space that a specific, checkable claim could occupy.',
          'Replace each one with something only you could say. "Passionate developer" becomes "Built the checkout flow 40k people use daily".',
          matched
        )
      );
    },
  }),

  defineRule({
    id: 'headline.default-only',
    section: 'headline',
    weight: 3,
    // Aimed squarely at the audience this tool exists for.
    appliesTo: ['student', 'jobSeeker'],
    evaluate(ctx) {
      const headline = find(ctx);
      if (!headline) return skip();

      // LinkedIn's own default for students is "Student at <University>". Left
      // as-is it tells a recruiter nothing except that you are enrolled.
      const isDefault =
        /^(?:student|undergraduate|postgraduate|graduate|studying|pursuing|final year|b\.?tech|m\.?tech|b\.?sc|m\.?sc|bca|mca|mba)\b/i.test(
          headline
        ) && charCount(headline) < 70;

      if (!isDefault) return pass();

      return flag(
        0.15,
        f(
          'default-only',
          'important',
          'Your headline only says that you are a student',
          'Thousands of profiles say "Student at …". It does not tell a recruiter what you can do, so there is no reason to click. This is the single biggest missed opportunity on student profiles.',
          'Name the work you want, not just your enrolment. "Student at Delhi University" becomes "CS Student | Building full-stack apps in React & Node | Seeking SDE internships".',
          [headline]
        )
      );
    },
  }),

  defineRule({
    id: 'headline.specificity',
    section: 'headline',
    weight: 2,
    evaluate(ctx) {
      const headline = find(ctx);
      if (!headline) return skip();

      // A headline that names a role but nothing else is a job title, not a
      // pitch. Separators or an explicit outcome verb signal a real structure.
      const hasSeparator = /[|•·—–]/.test(headline) || /\bat\b.+[,|]/i.test(headline);
      const hasOutcome =
        /\b(help|helping|build|building|turn|turning|deliver|delivering|solve|solving|grow|growing|reduce|reducing|design|designing|create|creating|scal\w+|automat\w+)\b/i.test(
          headline
        );

      if (hasSeparator || hasOutcome) return pass();

      return flag(
        0.5,
        f(
          'no-specialisation',
          'polish',
          'Your headline reads as a job title, not a pitch',
          'A bare title puts you in the same bucket as everyone with the same title. What distinguishes you is your specialisation and what you deliver.',
          'Add a second and third element separated by "|": your specialisation, then the outcome you produce.',
          [headline]
        )
      );
    },
  }),

  defineRule({
    id: 'headline.formatting',
    section: 'headline',
    weight: 1,
    evaluate(ctx) {
      const headline = find(ctx);
      if (!headline) return skip();

      const emoji = countEmoji(headline);
      const shouting = isShouting(headline);

      if (!shouting && emoji <= 2) return pass();

      const problems: string[] = [];
      if (shouting) problems.push('Written in all capitals');
      if (emoji > 2) problems.push(`${emoji} emoji`);

      return flag(
        0.4,
        f(
          'formatting',
          'polish',
          'Your headline formatting works against it',
          'All-capitals text is harder to read and is often flagged as spam, and a row of emoji pushes out the words a recruiter is actually searching for.',
          'Use sentence case and keep emoji to one or two at most — ideally none.',
          problems
        )
      );
    },
  }),
];
